import { type RefObject, useCallback, useMemo, useRef } from 'react';
import type { DateValue } from '@internationalized/date';
import type { DatePickerState, DateRangePickerState } from '@react-stately/datepicker';
import {
  type AriaDialogProps,
  type CalendarProps,
  DismissButton,
  Overlay,
  type Placement,
  type RangeCalendarProps,
  useDialog,
  usePopover,
} from 'react-aria';
import { Calendar } from '../calendar/calendar';
import { RangeCalendar } from '../calendar/range-calendar';
import { type DatePickerPagination, type DatePickerPopoverSize, getDatePickerCalendarStyles } from '../util';

interface DatePickerPopoverProps {
  calendarProps?: CalendarProps<DateValue>;
  calendarSize?: DatePickerPopoverSize;
  dialogProps: AriaDialogProps;
  locale: string;
  pagination: DatePickerPagination | undefined;
  parentDialog: HTMLElement | null;
  placement: Placement;
  rangeCalendarProps?: RangeCalendarProps<DateValue>;
  state: DatePickerState | DateRangePickerState;
  timeZone: string;
  triggerRef: RefObject<HTMLElement | null>;
}

export function DatePickerPopover({
  calendarProps,
  calendarSize,
  dialogProps,
  locale,
  pagination,
  parentDialog,
  placement,
  rangeCalendarProps,
  state,
  triggerRef,
  timeZone,
  ...rest
}: DatePickerPopoverProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const mappedDialogProps = useDialog(dialogProps, dialogRef);
  const { popoverProps, underlayProps } = usePopover(
    {
      ...rest,
      placement,
      offset: 8,
      popoverRef: ref,
      shouldFlip: true,
      triggerRef,
    },
    state,
  );

  const close = useCallback(() => {
    state.setOpen(false);
  }, [state.setOpen]);

  const underlayStyles = useMemo(
    () => ({
      ...underlayProps.style,
      display: state.isOpen ? 'fixed' : 'none',
      inset: 0,
    }),
    [state.isOpen, underlayProps.style],
  );

  const calendarStyles = getDatePickerCalendarStyles(triggerRef, calendarSize);

  return (
    <Overlay portalContainer={parentDialog || document.body}>
      <div {...underlayProps} style={underlayStyles} />
      <div {...popoverProps} ref={ref}>
        <DismissButton onDismiss={close} />
        <div hidden={!state.isOpen} ref={dialogRef} {...mappedDialogProps.dialogProps}>
          {calendarProps ? (
            <Calendar locale={locale} style={calendarStyles} {...calendarProps} isOpen={state.isOpen} pagination={pagination} timeZone={timeZone} />
          ) : null}
          {rangeCalendarProps ? <RangeCalendar locale={locale} style={calendarStyles} {...rangeCalendarProps} pagination={pagination} /> : null}
        </div>
        <DismissButton onDismiss={close} />
      </div>
    </Overlay>
  );
}
